/**
 * Credit Notes report — CN → invoice mapping keyed on the INVOICE date
 * (CN date is just a column). Summary tabs (Category / Salesperson /
 * Client Type / Monthly) + drilldown popover with tabbed filters +
 * flat mapping table + open (unapplied) credits.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import GlobalSyncPill from './GlobalSyncPill';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import api from '../../lib/api';
import { toast } from 'sonner';
import { RefreshCw, Loader2, FileWarning, Wallet, IndianRupee, FileText, Download, Layers, ChevronDown, Sparkles } from 'lucide-react';
import { SavedFilters, SpFilter } from '../../components/books/FilterKit';
import SlipInvoiceDetailModal from './SlipInvoiceDetailModal';
import CreditNoteExecutiveSummary from './CreditNoteExecutiveSummary';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmtInr = (n) => (n == null || isNaN(n)) ? '—' : `₹${Math.round(n).toLocaleString('en-IN')}`;
const ZOHO_ORG = '699857543';
const zohoInvUrl = (id) => `https://books.zoho.com/app/${ZOHO_ORG}/#/invoices/${id}`;
const zohoCnUrl = (id) => `https://books.zoho.com/app/${ZOHO_ORG}/#/creditnotes/${id}`;
const monthLabel = (mo) => mo && mo !== '—' ? new Date(`${mo}-01`).toLocaleString('en-IN', { month: 'short', year: 'numeric' }) : '—';

function computeRange(preset, monthKey) {
  const t = new Date(); const y = t.getFullYear(), m = t.getMonth();
  const fyStartYear = m >= 3 ? y : y - 1;
  const iso = (dt) => new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  if (preset === 'this_month') return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)), label: t.toLocaleString('en-IN', { month: 'short', year: 'numeric' }) };
  if (preset === 'last_month') return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)), label: 'Last month' };
  if (preset === 'this_fy') return { from: `${fyStartYear}-04-01`, to: `${fyStartYear + 1}-03-31`, label: `FY ${fyStartYear}-${String(fyStartYear + 1).slice(2)}` };
  if (preset === 'last_fy') return { from: `${fyStartYear - 1}-04-01`, to: `${fyStartYear}-03-31`, label: `FY ${fyStartYear - 1}-${String(fyStartYear).slice(2)}` };
  if (preset === 'all') return { from: '', to: '', label: 'All time' };
  if (preset === 'month' && monthKey) {
    const [yy, mm] = monthKey.split('-').map(Number);
    return { from: iso(new Date(yy, mm - 1, 1)), to: iso(new Date(yy, mm, 0)), label: new Date(yy, mm - 1, 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' }) };
  }
  return { from: `${y}-04-01`, to: iso(t), label: 'YTD' };
}

function buildMonthOptions() {
  const out = []; const now = new Date();
  let d = new Date(2025, 3, 1);
  while (d <= now) {
    out.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleString('en-IN', { month: 'short', year: 'numeric' }) });
    d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  }
  return out.reverse();
}

const GROUP_TABS = [
  { id: 'monthly', label: 'Monthly', field: null },
  { id: 'salesperson', label: 'By Salesperson', field: 'salesperson' },
  { id: 'trainer', label: 'By Trainer', field: 'trainer_name' },
  { id: 'created_by', label: 'By Created By', field: 'created_by_name' },
  { id: 'client_type', label: 'By Client Type', field: 'client_type' },
];

export default function CreditNotesPage() {
  const [preset, setPreset] = useState('this_fy');
  const [monthKey, setMonthKey] = useState(() => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`; });
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [tab, setTab] = useState('monthly');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState({ open: false, title: '', rows: [], openCredits: [] });
  const [drillGroup, setDrillGroup] = useState('none');
  const [drillExpanded, setDrillExpanded] = useState({});
  const [sync, setSync] = useState({ running: false, done: 0, total: 0, last_sync: null, cn_count: 0 });
  const [salespersons, setSalespersons] = useState([]);
  const [spState, setSpState] = useState({ op: 'is in', selected: [] });
  const [excludeRejected, setExcludeRejected] = useState(true);
  const [drillGroup2, setDrillGroup2] = useState('none');
  const [detailInvoice, setDetailInvoice] = useState({ open: false, invoiceId: null });
  const [sort, setSort] = useState({ key: 'credited_date', dir: 'desc' });
  const [clientType, setClientType] = useState('all');
  const [classifying, setClassifying] = useState(false);
  const [execOpen, setExecOpen] = useState(false);
  const monthOpts = useMemo(buildMonthOptions, []);

  const classifyReasons = async () => {
    setClassifying(true);
    try {
      const r = await api.post('/books/creditnotes/classify-reasons');
      toast.success(`Classified ${r.data.classified} refund reason(s)`);
      fetchReport();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Classification failed');
    } finally { setClassifying(false); }
  };

  const openInvoiceDetail = useCallback((invoiceId) => {
    if (invoiceId) setDetailInvoice({ open: true, invoiceId });
  }, []);

  const range = useMemo(() => preset === 'custom' ? { from: customFrom, to: customTo, label: 'Custom' } : computeRange(preset, monthKey), [preset, monthKey, customFrom, customTo]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.from) params.from_date = range.from;
      if (range.to) params.to_date = range.to;
      if (customerSearch) params.customer_search = customerSearch;
      if (clientType && clientType !== 'all') params.client_type = clientType;
      if (spState.selected.length > 0) { params.salesperson_op = spState.op; params.salesperson_names = JSON.stringify(spState.selected); }
      if (!excludeRejected) params.include_rejected = '1';
      const r = await api.get('/books/creditnotes/report', { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load credit notes');
    } finally { setLoading(false); }
  }, [range.from, range.to, customerSearch, spState, excludeRejected, clientType]);

  useEffect(() => {
    api.get('/books/metadata').then((r) => setSalespersons(r.data.salespersons || [])).catch(() => {});
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try { const r = await api.get('/books/creditnotes/sync-status'); setSync(r.data); return r.data; } catch { return null; }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);
  useEffect(() => { fetchSyncStatus(); }, [fetchSyncStatus]);

  useEffect(() => {
    if (!sync.running) return;
    const id = setInterval(async () => {
      const s = await fetchSyncStatus();
      if (s && !s.running) {
        clearInterval(id);
        toast.success('Credit notes sync complete');
        fetchReport();
      }
    }, 3000);
    return () => clearInterval(id);
  }, [sync.running, fetchSyncStatus, fetchReport]);

  const startSync = async (full = false) => {
    if (full && !window.confirm('Full re-sync re-fetches every credit note from Zoho and backfills creator, created date and refund reason for ALL records. This can take a few minutes. Continue?')) return;
    try {
      const r = await api.post(`/books/creditnotes/sync${full ? '?full=1' : ''}`);
      if (r.data.started) { toast.info(full ? 'Full re-sync started — re-fetching all credit notes & backfilling…' : 'Sync started — mapping credit notes to invoices…'); setSync((s) => ({ ...s, running: true, done: 0, total: 0 })); }
      else toast.info(r.data.message || 'Sync already running');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start sync'); }
  };

  const s = data?.summary;
  const groupRows = tab === 'monthly' ? (data?.monthly || []) : (data?.[`by_${tab}`] || []);
  const activeField = GROUP_TABS.find((g) => g.id === tab)?.field;

  const flatRows = data?.applications || [];
  const flatTotal = useMemo(() => flatRows.reduce((sum, r) => sum + (r.amount_applied || 0), 0), [flatRows]);

  // ── Unified, well-planned column model (shared by the mapping table + drill
  // modal). First column (Invoice #) is pinned left, Applied is pinned right,
  // and the header row is pinned to the top, so nothing is lost on scroll.
  const batchCell = (r) => r.batch_label
    ? <span title={`Batch ${r.batch_label}`}>{r.batch_label}</span>
    : <span className="text-slate-300">—</span>;

  const COLS = [
    { key: 'invoice', label: 'Invoice #', w: 128, sticky: 'left', sortKey: 'invoice_number' },
    { key: 'invoice_date', label: 'Invoice Date', w: 92 },
    { key: 'credited_date', label: 'CN Date', w: 92 },
    { key: 'customer_name', label: 'Customer', w: 150 },
    { key: 'enrollment_no', label: 'Enroll', w: 62 },
    { key: 'batch_label', label: 'Batch', w: 160 },
    { key: 'trainer_name', label: 'Trainer', w: 128 },
    { key: 'category', label: 'Category', w: 108 },
    { key: 'salesperson', label: 'Salesperson', w: 112 },
    { key: 'client_type', label: 'Client Type', w: 92 },
    { key: 'cn', label: 'CN #', w: 148, sortKey: 'creditnote_number' },
    { key: 'cn_reason', label: 'Refund Reason', w: 220 },
    { key: 'amount_applied', label: 'Applied', w: 112, align: 'right', sticky: 'right' },
  ];
  const GRID = COLS.map((c) => `${c.w}px`).join(' ');
  const GRID_MINW = COLS.reduce((sum, c) => sum + c.w, 0);
  const stickyCls = (c, bg) => c.sticky === 'left' ? `sticky left-0 z-10 ${bg}` : c.sticky === 'right' ? `sticky right-0 z-10 ${bg}` : '';

  const cellContent = (key, r) => {
    switch (key) {
      case 'invoice': return (
        <span className="inline-flex items-center gap-1 truncate">
          {r.invoice_id
            ? <a href={zohoInvUrl(r.invoice_id)} onClick={(e) => e.stopPropagation()} target="_blank" rel="noreferrer" className="text-violet-700 hover:underline font-medium">{r.invoice_number} ↗</a>
            : r.invoice_number}
          {r.invoice_cn_count > 1 && <span className="text-[8px] px-1 rounded bg-amber-100 text-amber-700 font-bold whitespace-nowrap" title={`This invoice received ${r.invoice_cn_count} credit notes`}>×{r.invoice_cn_count} CN</span>}
          {r.pair_app_count > 1 && <span className="text-[8px] px-1 rounded bg-teal-100 text-teal-700 font-bold whitespace-nowrap" title={`This credit note was applied to this invoice in ${r.pair_app_count} separate transactions`}>applied ×{r.pair_app_count}</span>}
        </span>
      );
      case 'cn': return (
        <span className="inline-flex items-center gap-1 truncate">
          <a href={zohoCnUrl(r.creditnote_id)} onClick={(e) => e.stopPropagation()} target="_blank" rel="noreferrer" className="text-rose-700 hover:underline font-medium">{r.creditnote_number} ↗</a>
          {r.cn_invoice_count > 1 && <span className="text-[8px] px-1 rounded bg-indigo-100 text-indigo-700 font-bold whitespace-nowrap" title={`This credit note is split across ${r.cn_invoice_count} invoices`}>split ×{r.cn_invoice_count}</span>}
        </span>
      );
      case 'amount_applied': return fmtInr(r.amount_applied);
      case 'batch_label': return batchCell(r);
      case 'enrollment_no': return r.enrollment_no || <span className="text-slate-300">—</span>;
      case 'trainer_name': return r.trainer_name || <span className="text-slate-300">—</span>;
      case 'cn_reason': return r.cn_reason || <span className="text-slate-300">—</span>;
      case 'invoice_date': return r.invoice_date || '—';
      case 'credited_date': return r.credited_date || '—';
      default: return r[key];
    }
  };

  const renderHeaderRow = () => (
    <div className="grid text-[9px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-100 border-b border-slate-200 sticky top-0 z-20" style={{ gridTemplateColumns: GRID }}>
      {COLS.map((c) => {
        const sk = c.sortKey || c.key;
        const active = sort.key === sk;
        return (
          <button key={c.key} type="button" onClick={() => toggleSort(sk)} data-testid={`cn-sorthdr-${sk}`}
            className={`px-2.5 py-2 whitespace-nowrap flex items-center gap-1 hover:text-slate-800 transition ${c.align === 'right' ? 'justify-end text-right' : ''} ${active ? 'text-rose-600' : ''} ${c.sticky ? `sticky ${c.sticky === 'left' ? 'left-0' : 'right-0'} z-30 bg-slate-100` : ''}`}>
            {c.label}<span className={`text-[8px] ${active ? '' : 'opacity-30'}`}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '▲'}</span>
          </button>
        );
      })}
    </div>
  );

  const renderTableRow = (r, key, testid) => (
    <div key={key} onClick={() => openInvoiceDetail(r.invoice_id)} className="group grid text-[11px] items-stretch border-b border-slate-100 hover:bg-rose-50/50 cursor-pointer transition-colors" style={{ gridTemplateColumns: GRID }} data-testid={testid}>
      {COLS.map((c) => (
        <div key={c.key}
          className={`px-2.5 py-2 truncate flex items-center ${c.align === 'right' ? 'justify-end text-right tabular-nums font-semibold text-rose-700' : ''} ${c.key === 'credited_date' ? 'text-slate-700 font-medium whitespace-nowrap' : c.key === 'invoice_date' ? 'text-slate-500 whitespace-nowrap' : 'text-slate-600'} ${c.sticky ? `sticky ${c.sticky === 'left' ? 'left-0' : 'right-0'} z-10 bg-white group-hover:bg-rose-50` : ''}`}
          title={['customer_name', 'trainer_name', 'category', 'salesperson', 'cn_reason'].includes(c.key) ? (r[c.key] || '') : undefined}>
          {cellContent(c.key, r)}
        </div>
      ))}
    </div>
  );

  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'amount_applied' ? 'desc' : 'asc' });

  // ─── Drilldown popover (group rows + KPI cards) ───
  const openDrill = (field, value, title) => {
    const rows = flatRows.filter((r) => field === 'month' ? (r.credited_date || '').slice(0, 7) === value : (r[field] || '') === value);
    setDrillGroup('none');
    setDrillGroup2('none');
    setDrillExpanded({});
    setDrill({ open: true, title, rows, openCredits: [] });
  };

  const openCardDrill = (type) => {
    setDrillGroup('none');
    setDrillGroup2('none');
    setDrillExpanded({});
    if (type === 'total') setDrill({ open: true, title: 'Total CN Value — applications + open credits', rows: flatRows, openCredits: data?.open_credits || [] });
    else if (type === 'applied') setDrill({ open: true, title: 'Applied to Invoices — all CN applications', rows: flatRows, openCredits: [] });
    else if (type === 'unapplied') setDrill({ open: true, title: 'Open / Unapplied Credit Notes', rows: [], openCredits: data?.open_credits || [] });
    else if (type === 'rejected') setDrill({ open: true, title: 'CN applications on REJECTED invoices (excluded from report)', rows: data?.rejected_applications || [], openCredits: [] });
  };

  const openDrillFilter = (predicate, title) => {
    setDrillGroup('none');
    setDrillGroup2('none');
    setDrillExpanded({});
    setDrill({ open: true, title, rows: flatRows.filter(predicate), openCredits: [] });
  };

  const cnKeyOf = (r, dim) => dim === 'month' ? ((r.credited_date || '—').slice(0, 7)) : (r[dim] || 'Not mentioned');

  const drillGroups = useMemo(() => {
    if (drillGroup === 'none') return null;
    const g2 = drillGroup2 !== 'none' && drillGroup2 !== drillGroup ? drillGroup2 : null;
    const m = new Map();
    for (const r of drill.rows) {
      const k1 = cnKeyOf(r, drillGroup);
      if (!m.has(k1)) m.set(k1, { key: k1, rows: [], applied: 0, subs: new Map() });
      const g = m.get(k1);
      g.rows.push(r);
      g.applied += r.amount_applied || 0;
      if (g2) {
        const k2 = cnKeyOf(r, g2);
        if (!g.subs.has(k2)) g.subs.set(k2, { key: k2, rows: [], applied: 0 });
        const s2 = g.subs.get(k2);
        s2.rows.push(r);
        s2.applied += r.amount_applied || 0;
      }
    }
    return [...m.values()].map((g) => ({ ...g, subs: [...g.subs.values()].sort((a, b) => b.applied - a.applied) })).sort((a, b) => b.applied - a.applied);
  }, [drill.rows, drillGroup, drillGroup2]);

  const drillTotal = useMemo(() => drill.rows.reduce((sum, r) => sum + (r.amount_applied || 0), 0), [drill.rows]);

  const renderDrillRow = (r, i) => renderTableRow(r, `${r.creditnote_id}-${r.invoice_id}-${i}`, `cn-drill-row-${i}`);

  const sortRows = (rows) => {
    const { key, dir } = sort; const m = dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (key === 'amount_applied') return ((a[key] || 0) - (b[key] || 0)) * m;
      return String(a[key] || '').localeCompare(String(b[key] || '')) * m;
    });
  };

  const topFromRows = (rows, field) => {
    const agg = {};
    for (const r of rows) {
      const k = r[field]; if (!k) continue;
      const e = agg[k] || (agg[k] = { name: k, cns: new Set(), applied: 0 });
      e.cns.add(r.creditnote_id); e.applied += r.amount_applied || 0;
    }
    return Object.values(agg).map((v) => ({ name: v.name, cn_count: v.cns.size, applied: v.applied }))
      .sort((a, b) => b.cn_count - a.cn_count || b.applied - a.applied).slice(0, 3);
  };

  const never = !sync.last_sync && !sync.running && (sync.cn_count === 0);

  const downloadCsv = () => {
    const headers = ['Invoice #', 'Invoice Date', 'CN Date', 'Customer', 'Enrollment No', 'Batch', 'Trainer', 'Category', 'Salesperson', 'Client Type', 'CN #', 'CN Status', 'Refund Reason', 'Applied (INR)'];
    const rows = sortRows(flatRows).map((r) => [r.invoice_number, r.invoice_date, r.credited_date, r.customer_name, r.enrollment_no, r.batch_label, r.trainer_name, r.category, r.salesperson, r.client_type, r.creditnote_number, r.cn_status, r.cn_reason, r.amount_applied]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'credit-notes.csv'; a.click(); URL.revokeObjectURL(url);
  };

  const exportDrillPdf = () => {
    const rows = sortRows(drill.rows);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pw = doc.internal.pageSize.getWidth();
    doc.setFillColor(190, 24, 93); doc.rect(0, 0, pw, 54, 'F');
    doc.setTextColor(255); doc.setFontSize(15); doc.setFont(undefined, 'bold');
    doc.text('Credit Notes — ' + (drill.title || 'Applications'), 40, 26);
    doc.setFontSize(9); doc.setFont(undefined, 'normal');
    doc.text(`${range.label} · Client type: ${clientType} · keyed on credit note date · ${rows.length} application(s)`, 40, 42);
    autoTable(doc, {
      startY: 66,
      head: [['Invoice #', 'Inv Date', 'CN Date', 'Customer', 'Enroll', 'Batch', 'Trainer', 'Salesperson', 'CN #', 'Refund Reason', 'Applied']],
      body: rows.map((r) => [r.invoice_number, r.invoice_date, r.credited_date, r.customer_name, r.enrollment_no, r.batch_label, r.trainer_name, r.salesperson, r.creditnote_number, r.cn_reason, `INR ${Math.round(r.amount_applied).toLocaleString('en-IN')}`]),
      foot: [['', '', '', '', '', '', '', '', '', 'TOTAL', `INR ${Math.round(drillTotal).toLocaleString('en-IN')}`]],
      styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak', valign: 'middle' },
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontSize: 7 },
      footStyles: { fillColor: [241, 245, 249], textColor: [190, 24, 93], fontStyle: 'bold' },
      columnStyles: { 9: { cellWidth: 150 }, 10: { halign: 'right' } },
      alternateRowStyles: { fillColor: [252, 245, 248] },
      margin: { left: 40, right: 40 },
    });
    doc.save(`credit-notes-${(drill.title || 'report').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`);
  };

  return (
    <div className="space-y-4" data-testid="credit-notes-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Credit Notes</h1>
          <p className="text-xs text-slate-500 mt-0.5">CN → invoice mapping · everything is keyed on the <b>credit note date</b> (when the credit was applied to the invoice)</p>
        </div>
        <div className="flex items-center gap-2">
          {sync.running ? (
            <Badge className="bg-violet-100 text-violet-700 text-[11px]" data-testid="cn-sync-progress"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Syncing {sync.done}/{sync.total || '…'}</Badge>
          ) : (
            <span className="text-[11px] text-slate-500">{sync.cn_count} CNs cached{sync.last_sync ? ` · ${new Date(sync.last_sync).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}</span>
          )}
          {/* Unified sync via the Books Analytics shared pill (invoices → payments → CN → CRM leads). */}
          <GlobalSyncPill />
          <Button size="sm" onClick={() => setExecOpen(true)} className="h-8 text-xs bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white border-0 shadow-sm" data-testid="cn-exec-open-btn" title="Revenue leakage, accountability & watchlists">
            <Sparkles className="w-3 h-3 mr-1.5" />Executive Summary
          </Button>
          <Button size="sm" variant="outline" onClick={() => startSync(true)} disabled={sync.running} className="h-8 text-xs" data-testid="cn-full-resync-btn" title="Re-fetch every credit note from Zoho and backfill creator, created date & refund reason">
            <RefreshCw className={`w-3 h-3 mr-1.5 ${sync.running ? 'animate-spin' : ''}`} />Full re-sync
          </Button>
          <Button size="sm" variant="ghost" onClick={classifyReasons} disabled={classifying} className="h-8 text-xs" data-testid="cn-classify-btn" title="AI-classify refund reasons into themes for the executive summary">
            <Sparkles className={`w-3 h-3 mr-1.5 ${classifying ? 'animate-spin' : ''}`} />Classify reasons
          </Button>
          <Button size="sm" variant="ghost" onClick={fetchReport} className="h-8 text-xs" data-testid="cn-reload-btn"><RefreshCw className="w-3 h-3 mr-1.5" />Reload</Button>
        </div>
      </div>

      {never && (
        <Card className="p-4 border-2 border-dashed border-violet-200 bg-violet-50/40 text-center">
          <p className="text-sm text-slate-700 font-medium">No credit notes synced yet.</p>
          <p className="text-xs text-slate-500 mt-1">Click <b>Sync</b> above (top-right pill) — it runs Invoices → Payments → Credit Notes → CRM leads sequentially.</p>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-3 border border-border">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-1">Range</span>
          {[['this_month', 'This month'], ['last_month', 'Last month'], ['this_fy', 'This FY'], ['last_fy', 'Last FY'], ['ytd', 'YTD'], ['all', 'All time'], ['month', 'Specific month'], ['custom', 'Custom']].map(([id, label]) => (
            <button key={id} onClick={() => setPreset(id)} data-testid={`cn-preset-${id}`}
              className={`text-[11px] px-2.5 py-1 rounded-md border transition ${preset === id ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{label}</button>
          ))}
          {preset === 'month' && (
            <select value={monthKey} onChange={(e) => setMonthKey(e.target.value)} className="text-[11px] border border-slate-200 rounded-md px-2 py-1" data-testid="cn-month-select">
              {monthOpts.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          )}
          {preset === 'custom' && (
            <span className="flex items-center gap-1">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="text-[11px] border border-slate-200 rounded-md px-1.5 py-1" data-testid="cn-custom-from" />
              <span className="text-[10px] text-slate-400">→</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="text-[11px] border border-slate-200 rounded-md px-1.5 py-1" data-testid="cn-custom-to" />
            </span>
          )}
          <span className="text-[10px] text-slate-400 ml-1">{range.from || '…'} → {range.to || '…'}</span>
          <div className="ml-auto flex items-center gap-2">
            <SpFilter all={salespersons} state={spState} setState={setSpState} />
            <button
              type="button"
              onClick={() => setExcludeRejected(!excludeRejected)}
              data-testid="cn-toggle-rejected"
              title="CN applications on rejected invoices are excluded by default"
              className={`text-[11px] px-2.5 py-1.5 rounded-md border transition whitespace-nowrap ${excludeRejected ? 'bg-emerald-50 border-emerald-200 text-emerald-700 font-medium' : 'bg-amber-50 border-amber-300 text-amber-700 font-medium'}`}
            >
              {excludeRejected ? '✓ Rejected excluded' : '⚠ Rejected included'}
            </button>
            <button type="button" onClick={() => openCardDrill('rejected')} data-testid="cn-view-rejected" className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 whitespace-nowrap">View rejected{data?.rejected_applications?.length ? ` (${data.rejected_applications.length})` : ''}</button>
            <div className="flex items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Client</span>
              <select value={clientType} onChange={(e) => setClientType(e.target.value)} data-testid="cn-client-type-filter"
                className="text-[11px] border border-slate-200 rounded-md px-2 py-1.5 bg-white font-medium text-slate-700">
                <option value="all">All types</option>
                {(data?.client_type_options?.length ? data.client_type_options : ['Retail']).map((ct) => <option key={ct} value={ct}>{ct}</option>)}
                {data && !data.client_type_options?.includes('Retail') && <option value="Retail">Retail</option>}
              </select>
            </div>
            <Input placeholder="Customer…" value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} className="h-7 w-44 text-xs" data-testid="cn-customer-search" />
          </div>
        </div>
      </Card>

      <SavedFilters
        storageKey="creditNotesSavedFilters"
        getCurrent={() => ({ preset, monthKey, customFrom, customTo, customerSearch, spState, excludeRejected, clientType })}
        onApply={(f) => {
          if (f.preset) setPreset(f.preset);
          if (f.monthKey) setMonthKey(f.monthKey);
          setCustomFrom(f.customFrom ?? ''); setCustomTo(f.customTo ?? '');
          setCustomerSearch(f.customerSearch ?? '');
          setSpState(f.spState ?? { op: 'is in', selected: [] });
          setExcludeRejected(f.excludeRejected ?? true);
          setClientType(f.clientType ?? 'all');
        }}
      />

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
      ) : !data ? null : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-4 border cursor-pointer hover:border-rose-300 hover:shadow-md transition" onClick={() => openCardDrill('total')} role="button" data-testid="cn-kpi-total"><div className="w-9 h-9 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center"><FileWarning className="w-4 h-4" /></div><p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mt-3">Total CN Value</p><p className="text-2xl font-bold text-slate-900 mt-0.5">{fmtInr(s.cn_total)}</p><p className="text-[11px] text-slate-500">{s.cn_count} credit notes · click to drill</p></Card>
            <Card className="p-4 border cursor-pointer hover:border-violet-300 hover:shadow-md transition" onClick={() => openCardDrill('applied')} role="button" data-testid="cn-kpi-applied"><div className="w-9 h-9 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center"><FileText className="w-4 h-4" /></div><p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mt-3">Applied to Invoices</p><p className="text-2xl font-bold text-slate-900 mt-0.5">{fmtInr(s.applied_total)}</p><p className="text-[11px] text-slate-500">{s.application_count} applications · {s.invoices_touched} invoices · click to drill</p></Card>
            <Card className="p-4 border cursor-pointer hover:border-amber-300 hover:shadow-md transition" onClick={() => openCardDrill('unapplied')} role="button" data-testid="cn-kpi-unapplied"><div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><Wallet className="w-4 h-4" /></div><p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mt-3">Unapplied Balance</p><p className="text-2xl font-bold text-slate-900 mt-0.5">{fmtInr(s.unapplied_total)}</p><p className="text-[11px] text-slate-500">{s.open_cn_count} open CN(s) · click to drill</p></Card>
            <Card className="p-4 border" data-testid="cn-kpi-window"><div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><IndianRupee className="w-4 h-4" /></div><p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mt-3">Window</p><p className="text-2xl font-bold text-slate-900 mt-0.5">{range.label}</p><p className="text-[11px] text-slate-500">{clientType === 'all' ? 'all client types' : clientType} · CN date</p></Card>
          </div>

          {/* Top CN by Trainer / Salesperson */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[['top_trainers', 'Top Credit Notes by Trainer', 'trainer_name', 'bg-rose-500'], ['top_salespersons', 'Top Credit Notes by Salesperson', 'salesperson', 'bg-violet-500']].map(([dataKey, title, field, accent]) => {
              const list = data[dataKey] || [];
              const lead = list[0];
              const maxCn = Math.max(...list.map((x) => x.cn_count), 1);
              return (
                <Card key={dataKey} className="p-4 border border-border" data-testid={`cn-top-${field}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</h3>
                    <span className="text-[10px] text-slate-400">count of credit notes</span>
                  </div>
                  {!lead ? <p className="text-xs text-slate-400 py-4 text-center">No data</p> : (
                    <div className="space-y-1.5">
                      {list.map((t, i) => (
                        <button key={t.name} type="button" onClick={() => openDrill(field, t.name, `${title.replace('Top Credit Notes by ', '')}: ${t.name}`)}
                          data-testid={`cn-top-${field}-row-${i}`}
                          className="w-full flex items-center gap-2 group text-left hover:bg-slate-50 rounded-md px-1 py-0.5 transition">
                          <span className={`w-4 text-center text-[10px] font-bold ${i === 0 ? 'text-rose-600' : 'text-slate-400'}`}>{i + 1}</span>
                          <span className="w-40 truncate text-[11px] font-medium text-slate-700 group-hover:text-slate-900" title={t.name}>{t.name}</span>
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full ${accent} rounded-full`} style={{ width: `${(t.cn_count / maxCn) * 100}%` }} /></div>
                          <span className="w-10 text-right text-[11px] font-bold text-slate-800 tabular-nums">{t.cn_count}</span>
                          <span className="w-20 text-right text-[10px] text-slate-500 tabular-nums">{fmtInr(t.applied)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Executive summary — opened from the header button below */}
          <CreditNoteExecutiveSummary exec={data.executive} summary={s} rangeLabel={range.label} clientType={clientType} fmtInr={fmtInr} onOpenInvoice={openInvoiceDetail} onOpenTheme={(theme) => openDrillFilter((r) => (r.reason_theme || 'Unclassified') === theme, `Credit notes — "${theme}"`)} open={execOpen} onOpenChange={setExecOpen} />

          {/* CN ↔ Invoice relationship legend — chips open the matching applications */}
          {data.relationship && (
            <Card className="p-3 border border-border" data-testid="cn-relationship-legend">
              <div className="flex items-center gap-2 flex-wrap text-[11px]">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-1">CN ↔ Invoice</span>
                <button type="button" onClick={() => openDrillFilter((r) => r.cn_invoice_count === 1 && r.invoice_cn_count === 1 && r.pair_app_count === 1, 'Simple 1 CN → 1 invoice applications')}
                  data-testid="cn-rel-single" className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-50 border border-slate-200 hover:bg-slate-100 transition">
                  <b className="text-slate-800 tabular-nums">{data.relationship.cn_single}</b> CNs → 1 invoice each
                </button>
                <button type="button" onClick={() => openDrillFilter((r) => r.cn_invoice_count > 1, 'Credit notes split across multiple invoices')}
                  data-testid="cn-rel-split" className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition disabled:opacity-50" disabled={!data.relationship.cn_multi}>
                  <span className="text-[8px] px-1 rounded bg-indigo-100 text-indigo-700 font-bold">split ×N</span>
                  <b className="text-indigo-800 tabular-nums">{data.relationship.cn_multi}</b> CNs split across multiple invoices
                </button>
                <button type="button" onClick={() => openDrillFilter((r) => r.invoice_cn_count > 1, 'Invoices that received multiple credit notes')}
                  data-testid="cn-rel-multicn" className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 hover:bg-amber-100 transition disabled:opacity-50" disabled={!data.relationship.inv_multi}>
                  <span className="text-[8px] px-1 rounded bg-amber-100 text-amber-700 font-bold">×N CN</span>
                  <b className="text-amber-800 tabular-nums">{data.relationship.inv_multi}</b> invoices received multiple CNs
                </button>
                {data.relationship.repeat_applications > 0 && (
                  <button type="button" onClick={() => openDrillFilter((r) => r.pair_app_count > 1, 'Repeat applications — same CN re-applied to same invoice')}
                    data-testid="cn-rel-repeat" className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-teal-50 border border-teal-200 hover:bg-teal-100 transition">
                    <span className="text-[8px] px-1 rounded bg-teal-100 text-teal-700 font-bold">applied ×N</span>
                    <b className="text-teal-800 tabular-nums">{data.relationship.repeat_applications}</b> repeat applications (same CN re-applied to same invoice)
                  </button>
                )}
                <span className="text-[10px] text-slate-400 ml-auto">
                  {data.relationship.cn_applied_total} applied CNs · {data.relationship.distinct_pairs} CN→invoice links · {s.application_count} applications · {data.relationship.invoices_total} invoices
                </span>
              </div>
            </Card>
          )}

          {/* Group tabs */}
          <Card className="p-4 border border-border">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="h-8">
                {GROUP_TABS.map((g) => <TabsTrigger key={g.id} value={g.id} className="text-xs h-7" data-testid={`cn-tab-${g.id}`}>{g.label}</TabsTrigger>)}
              </TabsList>
            </Tabs>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs" data-testid="cn-group-table">
                <thead>
                  <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                    <th className="text-left px-2.5 py-2 font-semibold">{tab === 'monthly' ? 'CN Month' : GROUP_TABS.find((g) => g.id === tab)?.label.replace('By ', '')}</th>
                    <th className="text-right px-2.5 py-2 font-semibold">CN Applied</th>
                    <th className="text-right px-2.5 py-2 font-semibold">Applications</th>
                    <th className="text-left px-2.5 py-2 font-semibold w-1/3">Share <span className="normal-case font-normal text-slate-400">(% of total ₹)</span></th>
                  </tr>
                </thead>
                <tbody>
                  {groupRows.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-slate-400 py-6">No credit-note applications in this window.</td></tr>
                  ) : groupRows.map((g) => {
                    const key = tab === 'monthly' ? g.month : g.group;
                    const label = tab === 'monthly' ? monthLabel(g.month) : (g.group || '— Unassigned —');
                    const testKey = key || 'unassigned';
                    const totalV = groupRows.reduce((sm, x) => sm + x.applied, 0) || 1;
                    const pct = (g.applied / totalV) * 100;
                    return (
                      <tr key={testKey}
                        onClick={() => openDrill(tab === 'monthly' ? 'month' : activeField, key, `${tab === 'monthly' ? 'CN Month' : GROUP_TABS.find((x) => x.id === tab)?.label.replace('By ', '')}: ${label}`)}
                        className="border-t border-slate-100 cursor-pointer hover:bg-violet-50/40" data-testid={`cn-group-row-${testKey}`}>
                        <td className="px-2.5 py-2 font-medium text-slate-800">{label}</td>
                        <td className="text-right px-2.5 py-2 tabular-nums font-semibold text-rose-700">{fmtInr(g.applied)}</td>
                        <td className="text-right px-2.5 py-2 tabular-nums text-slate-500">{g.count}</td>
                        <td className="px-2.5 py-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-rose-400 rounded-full" style={{ width: `${Math.round(pct)}%` }} /></div>
                            <span className="w-10 text-right text-[10px] tabular-nums text-slate-500">{pct.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 border-t-2 border-slate-300 text-[11px] font-bold cursor-pointer hover:bg-slate-200/70" onClick={() => openCardDrill('applied')} data-testid="cn-group-total-row">
                    <td className="px-2.5 py-2 text-slate-800">GRAND TOTAL <span className="text-[10px] font-normal text-slate-500">· {groupRows.length} group(s)</span></td>
                    <td className="text-right px-2.5 py-2 tabular-nums text-rose-700">{fmtInr(groupRows.reduce((sm, g) => sm + g.applied, 0))}</td>
                    <td className="text-right px-2.5 py-2 tabular-nums text-slate-600">{groupRows.reduce((sm, g) => sm + (g.count || 0), 0)}</td>
                    <td className="px-2.5 py-2 text-[10px] font-normal text-slate-400">click to view all applications</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Click a row to open the CN applications behind it (with its own group-by filters) · attribution comes from the invoice the CN was applied to.</p>
          </Card>
        </>
      )}

      {/* ─── Drilldown popover with tabbed group filters ─── */}
      <Dialog open={drill.open} onOpenChange={(o) => setDrill((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-6xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="cn-drill-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{drill.title}</DialogTitle>
            <p className="text-[11px] text-slate-500 mt-0.5">{drill.rows.length} CN application(s) · {range.label} · keyed on credit note date</p>
          </DialogHeader>
          {drill.rows.length > 0 && (
            <div className="grid grid-cols-2 gap-2 pb-2">
              {[['Trainer', 'trainer_name'], ['Salesperson', 'salesperson']].map(([lbl, field]) => {
                const top = topFromRows(drill.rows, field);
                return (
                  <div key={field} className="border border-slate-200 rounded-md p-2 bg-slate-50/60" data-testid={`cn-modal-top-${field}`}>
                    <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Top by {lbl} (CN count)</p>
                    {top.length === 0 ? <p className="text-[10px] text-slate-400">—</p> : top.map((t, i) => (
                      <div key={t.name} className="flex items-center gap-1.5 text-[10.5px] py-0.5">
                        <span className={`w-3 text-center font-bold ${i === 0 ? 'text-rose-600' : 'text-slate-400'}`}>{i + 1}</span>
                        <span className="flex-1 truncate text-slate-700" title={t.name}>{t.name}</span>
                        <span className="font-bold text-slate-800 tabular-nums">{t.cn_count}</span>
                        <span className="text-slate-500 tabular-nums w-16 text-right">{fmtInr(t.applied)}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 pb-2 border-b border-slate-100 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <Layers className="w-3 h-3 text-slate-400" />
              <span className="text-[10px] text-slate-500 mr-1">Group by</span>
              {[['none', 'None'], ['category', 'Category'], ['salesperson', 'Salesperson'], ['client_type', 'Client Type'], ['trainer_name', 'Trainer'], ['created_by_name', 'Created By'], ['month', 'CN Month']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => { setDrillGroup(v); setDrillExpanded({}); }} data-testid={`cn-drill-group-${v}`}
                  className={`text-[11px] px-2 py-1 rounded-md border transition ${drillGroup === v ? 'bg-rose-600 text-white border-rose-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
              ))}
              {drillGroup !== 'none' && (
                <>
                  <span className="w-px h-4 bg-slate-200 mx-1" />
                  <span className="text-[10px] text-slate-500">Then by</span>
                  {[['none', 'None'], ['category', 'Category'], ['salesperson', 'Salesperson'], ['client_type', 'Client Type'], ['trainer_name', 'Trainer'], ['created_by_name', 'Created By'], ['month', 'CN Month']].filter(([v]) => v === 'none' || v !== drillGroup).map(([v, l]) => (
                    <button key={v} type="button" onClick={() => { setDrillGroup2(v); setDrillExpanded({}); }} data-testid={`cn-drill-group2-${v}`}
                      className={`text-[11px] px-2 py-1 rounded-md border transition ${drillGroup2 === v ? 'bg-rose-500 text-white border-rose-500' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
                  ))}
                </>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="ghost" onClick={downloadCsv} className="h-7 text-[11px]" data-testid="cn-drill-csv-btn"><Download className="w-3 h-3 mr-1" />CSV</Button>
              <Button size="sm" variant="outline" onClick={exportDrillPdf} className="h-7 text-[11px]" data-testid="cn-drill-pdf-btn" disabled={drill.rows.length === 0}><FileText className="w-3 h-3 mr-1" />PDF</Button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto -mx-6 px-6 pt-2">
            <div className="bg-white border border-slate-200 rounded-lg overflow-auto max-h-[60vh]">
              <div style={{ minWidth: `${GRID_MINW}px` }}>
              {renderHeaderRow()}
              {drill.rows.length === 0 ? (
                drill.openCredits.length === 0 && <p className="text-xs text-slate-400 py-8 text-center">No CN applications.</p>
              ) : drillGroups === null
                ? sortRows(drill.rows).map((r, i) => renderDrillRow(r, i))
                : drillGroups.map((g) => (
                  <div key={g.key}>
                    <button
                      type="button"
                      onClick={() => setDrillExpanded((p) => ({ ...p, [g.key]: !p[g.key] }))}
                      className="w-full grid bg-rose-50/70 border-b border-rose-100 text-[11px] items-center font-semibold text-left hover:bg-rose-100/60 transition cursor-pointer"
                      style={{ gridTemplateColumns: GRID }}
                      data-testid={`cn-drill-grp-${g.key}`}
                    >
                      <div className="px-2.5 py-1.5 text-rose-900 truncate flex items-center gap-1 sticky left-0 z-10 bg-rose-50" style={{ gridColumn: '1 / -2' }}>
                        <ChevronDown className={`w-3 h-3 shrink-0 text-rose-500 transition-transform ${drillExpanded[g.key] ? '' : '-rotate-90'}`} />
                        {drillGroup === 'month' ? monthLabel(g.key) : (g.key || '— Unassigned —')} <span className="text-[10px] font-normal text-slate-500">· {g.rows.length} application(s)</span>
                      </div>
                      <div className="px-2.5 py-1.5 text-right text-rose-800 tabular-nums sticky right-0 z-10 bg-rose-50">{fmtInr(g.applied)}</div>
                    </button>
                    {drillExpanded[g.key] && (g.subs.length > 0
                      ? g.subs.map((s2) => {
                        const sk = `${g.key}||${s2.key}`;
                        return (
                          <div key={sk}>
                            <button
                              type="button"
                              onClick={() => setDrillExpanded((p) => ({ ...p, [sk]: !p[sk] }))}
                              className="w-full grid bg-rose-50/40 border-b border-rose-50 text-[11px] items-center font-medium text-left hover:bg-rose-100/40 transition cursor-pointer"
                              style={{ gridTemplateColumns: GRID }}
                              data-testid={`cn-drill-subgrp-${sk}`}
                            >
                              <div className="px-2.5 py-1 text-rose-800 truncate flex items-center gap-1 pl-5 sticky left-0 z-10 bg-rose-50/60" style={{ gridColumn: '1 / -2' }}>
                                <ChevronDown className={`w-3 h-3 shrink-0 text-rose-400 transition-transform ${drillExpanded[sk] ? '' : '-rotate-90'}`} />
                                {drillGroup2 === 'month' ? monthLabel(s2.key) : (s2.key || '— Unassigned —')} <span className="text-[10px] font-normal text-slate-500">· {s2.rows.length} application(s)</span>
                              </div>
                              <div className="px-2.5 py-1 text-right text-rose-700 tabular-nums sticky right-0 z-10 bg-rose-50/60">{fmtInr(s2.applied)}</div>
                            </button>
                            {drillExpanded[sk] && sortRows(s2.rows).map((r, i) => renderDrillRow(r, `${sk}-${i}`))}
                          </div>
                        );
                      })
                      : sortRows(g.rows).map((r, i) => renderDrillRow(r, `${g.key}-${i}`)))}
                  </div>
                ))}
              {drill.rows.length > 0 && (
                <div className="grid bg-slate-100 border-t-2 border-slate-300 text-[11px] items-center font-bold sticky bottom-0 z-10" style={{ gridTemplateColumns: GRID }} data-testid="cn-drill-total-row">
                  <div className="px-2.5 py-2.5 text-slate-800 sticky left-0 z-10 bg-slate-100" style={{ gridColumn: '1 / -2' }}>GRAND TOTAL <span className="text-[10px] font-normal text-slate-500">· {drill.rows.length} application(s)</span></div>
                  <div className="px-2.5 py-2.5 text-right tabular-nums text-rose-700 sticky right-0 z-10 bg-slate-100">{fmtInr(drillTotal)}</div>
                </div>
              )}
              </div>
            </div>
            {drill.openCredits.length > 0 && (
              <div className="mt-3 bg-white border border-amber-200 rounded overflow-hidden" data-testid="cn-drill-open-credits">
                <div className="px-3 py-1.5 bg-amber-50 text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Open / Unapplied credit notes · by CN date</div>
                {drill.openCredits.map((c) => (
                  <div key={c.creditnote_id} className="grid grid-cols-8 gap-2 px-3 py-1.5 border-t border-slate-100 text-[11px] items-center">
                    <div><a href={zohoCnUrl(c.creditnote_id)} target="_blank" rel="noreferrer" className="text-rose-700 hover:underline font-medium whitespace-nowrap">{c.creditnote_number} ↗</a></div>
                    <div className="text-slate-500">{c.cn_date}</div>
                    <div className="col-span-3 text-slate-700 truncate" title={c.customer_name}>{c.customer_name}</div>
                    <div className="text-slate-400 truncate" title={c.reason}>{c.reason || '—'}</div>
                    <div className="text-right tabular-nums text-slate-600">{fmtInr(c.total)}</div>
                    <div className="text-right tabular-nums font-semibold text-amber-700">{fmtInr(c.balance)}</div>
                  </div>
                ))}
                <div className="grid grid-cols-8 gap-2 px-3 py-2 bg-amber-50/60 border-t-2 border-amber-200 text-[11px] font-bold">
                  <div className="col-span-7 text-slate-800">UNAPPLIED TOTAL <span className="text-[10px] font-normal text-slate-500">· {drill.openCredits.length} CN(s)</span></div>
                  <div className="text-right tabular-nums text-amber-700">{fmtInr(drill.openCredits.reduce((sum, c) => sum + (c.balance || 0), 0))}</div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <SlipInvoiceDetailModal
        open={detailInvoice.open}
        onOpenChange={(o) => setDetailInvoice((d) => ({ ...d, open: o }))}
        invoiceId={detailInvoice.invoiceId}
        zohoOrgId={ZOHO_ORG}
      />
    </div>
  );
}
